import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import { MASTER_API, PURCHASE_API } from "../../apiConfig";
import sbeeLogo from "../../assets/sbeelogo.png";
import html2pdf from "html2pdf.js";

const QUALITY_INSP_API = `${PURCHASE_API}/quality-inspection`;

// Quantities come back from the DB as fixed 3-decimal strings (e.g.
// "125.900", "6.370"). For display, cap at 2 decimal places and drop a
// trailing ".00"/".0" entirely (so "125.900" -> "125.9", "50.000" -> "50").
// Non-numeric values (blank, text) are returned unchanged.
const formatQty = (val) => {
  if (val === null || val === undefined || val === "") return val;
  const num = Number(val);
  if (Number.isNaN(num)) return val;
  return num.toFixed(2).replace(/\.?0+$/, "");
};

const RawMaterialInspectionReportView = forwardRef(({ inspectionNo: propInspectionNo, stockId, testType, loggedInUser }, ref) => {
  const params = useParams();
  const inspectionNo = decodeURIComponent(propInspectionNo || params.inspectionNo);

  const [reportData, setReportData] = useState([]);
  // Sample column labels for this batch — set via the "Sample No" popup in
  // the Quality Inspection modal (avPayload.sample_labels). Defaults to the
  // 3-sample layout when a batch has no custom config yet. Previously this
  // view hardcoded 3 sample columns (sample1/sample2/sample3), so setting
  // Sample No to 4 in the edit modal never showed the 4th sample here.
  const [sampleLabels, setSampleLabels] = useState(["Sample. 1", "Sample. 2", "Sample. 3"]);
  const [extraHeader, setExtraHeader] = useState({ wi_no: "", ref_no: "", invoice_no: "", grn_number: "", po_number: "", lot_no: 3, updated_at: "", tested_by: "", witnessed_by: "", witnessed_designation: "" });
  const [stockHeader, setStockHeader] = useState({
    product_code: "", product_description: "", batch_number: "", qty: "",
    prd_no: "", customer_name: "", grn_number: "", po_number: "",
    supplier_name: "", category: "", sub_category: "", qa_category: "",
    status: "", grn_date: "",
  });
  const [revDate, setRevDate] = useState(null);
  const reportRef = useRef();

  // Fall back to the app's actual auth storage if the parent didn't pass loggedInUser as a prop.
  // localStorage key is "user" → JSON like { email, role, userId, ... }.
  const getStoredUserName = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      // Prefer the actual user id (e.g. "maithili") so this stays
      // consistent with Purchase Request / Goods Receipt / Purchase Order /
      // Audit Calendar, which all store userId rather than a name or email.
      if (stored.userId) return stored.userId;
      if (stored.name) return stored.name;
      if (stored.username) return stored.username;
      // No display name stored — use only the part before "@" from the email,
      // so the report shows e.g. "admin" instead of "admin@example.com".
      if (stored.email) return String(stored.email).split("@")[0];
      return "";
    } catch {
      return "";
    }
  };
  const checkedByName = loggedInUser || getStoredUserName();
  const isFullyInspected = (stockHeader.status || "").toLowerCase() === "inspected";
  const approvedByName = isFullyInspected ? checkedByName : "";

  useEffect(() => {
    loadReport();
  }, [inspectionNo, stockId, testType]);

  // fetch() doesn't go through the app's global axios interceptor (that
  // one only patches axios itself, in apiConfig.js), so every fetch() call
  // below needs the Authorization header attached manually or the
  // now-guarded quality-inspection routes (stock-header, batch-actuals)
  // 401 silently — fetch() doesn't throw on non-2xx, so the error body was
  // being treated as real data, leaving every field blank except
  // CHECKED BY (which comes from the loggedInUser prop, not an API call).
  const getAuthHeaders = () => {
    let token = localStorage.getItem("token");
    if (!token) {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        token = user?.token || null;
      } catch (e) {
        token = null;
      }
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadReport = async () => {
    try {
      const encodedNo = inspectionNo.includes("__")
        ? inspectionNo
        : inspectionNo.replace(/\//g, "__");

      if (stockId) {
        // Step 1: fetch actuals + stock-header in parallel
        const [avRes, shRes] = await Promise.all([
          fetch(`${QUALITY_INSP_API}/batch-actuals/${stockId}${testType ? `?testType=${encodeURIComponent(testType)}` : ""}`, { headers: getAuthHeaders() }),
          fetch(`${QUALITY_INSP_API}/stock-header/${stockId}`, { headers: getAuthHeaders() }),
        ]);
        const avPayload = await avRes.json();
        const shPayload = await shRes.json();

        if (!shRes.ok || !avRes.ok) {
          console.error(
            "RawMaterialInspectionReportView: stock-header/batch-actuals request failed",
            { shStatus: shRes.status, shPayload, avStatus: avRes.status, avPayload }
          );
        }

        const productCode = shPayload.product_code || "";

        setStockHeader({
          product_code:        shPayload.product_code        || "",
          product_description: shPayload.product_description || "",
          batch_number:        shPayload.batch_number        || "",
          qty:                 shPayload.qty                 ?? "",
          prd_no:              shPayload.prd_no              || "",
          customer_name:       shPayload.customer_name       || "",
          grn_number:          shPayload.grn_number          || "",
          po_number:           shPayload.po_number           || "",
          supplier_name:       shPayload.supplier_name       || "",
          category:            shPayload.category            || "",
          sub_category:        shPayload.sub_category        || "",
          qa_category:         shPayload.qa_category         || "",
          status:              shPayload.status              || "",
          grn_date:            shPayload.grn_date            || "",
        });

        console.log("🔍 stock-header payload:", JSON.stringify(shPayload));
        const avList = Array.isArray(avPayload) ? avPayload : (avPayload.actuals || []);

        const labels = Array.isArray(avPayload.sample_labels) && avPayload.sample_labels.length > 0
          ? avPayload.sample_labels
          : ["Sample. 1", "Sample. 2", "Sample. 3"];
        setSampleLabels(labels);

        setExtraHeader({
          wi_no:      avPayload.wi_no      || "",
          ref_no:     avPayload.ref_no     || "",
          invoice_no: avPayload.invoice_no || "",
          grn_number: avPayload.grn_number || shPayload.grn_number || "",
          po_number:  avPayload.po_number  || shPayload.po_number  || "",
          lot_no:     avPayload.lot_no     || 3,
          tested_by:  avPayload.tested_by  || "",
          witnessed_by: avPayload.witnessed_by || "",
          witnessed_designation: avPayload.witnessed_designation || "",
        });
        setRevDate(avPayload.last_updated_at || null);

        // Step 2: fetch inspection structure WITH productCode so we get real IDs
        // from inspection_product_format (not the null-id JSON fallback)
        const reportUrl = productCode
          ? `${MASTER_API}/inspection/report/${encodedNo}?productCode=${encodeURIComponent(productCode)}${testType ? `&testType=${encodeURIComponent(testType)}` : ""}`
          : `${MASTER_API}/inspection/report/${encodedNo}`;
        const res = await fetch(reportUrl, { headers: getAuthHeaders() });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];

        // Step 3: build avMap keyed by characteristic_id from batch_inspection_actuals
        const avMap = {};
        avList.forEach((av) => {
          const values = Array.isArray(av.sample_values) && av.sample_values.length > 0
            ? av.sample_values
            : [av.sample1 || "", av.sample2 || "", av.sample3 || ""];
          avMap[av.characteristic_id] = {
            sample_values: values,
            manual_result: av.manual_result || "",
            remarks: av.remarks || "",
          };
        });

        // Step 4: merge — rows now have real `id` from inspection_product_format
        const merged = rows.map((r) => {
          const rowId = r.id ?? r.characteristic_id;
          const actuals = avMap[rowId];
          return actuals
            ? { ...r, sample_values: actuals.sample_values, manual_result: actuals.manual_result, remarks: actuals.remarks }
            : { ...r, sample_values: labels.map(() => ""), manual_result: "", remarks: "" };
        });

        // Only include characteristics that actually have a Standard Value
        // configured — same rule as the Edit modal in Quality.js.
        // Only parameters that were ticked (= saved actuals exist) in the
        // inspection modal appear on the report.
        const withStdValue = merged.filter(
          (r) => r.standard_value !== null && r.standard_value !== undefined && String(r.standard_value).trim() !== ""
            && avMap[r.id ?? r.characteristic_id]
        );
        setReportData(withStdValue);

      } else {
        // No stockId — fetch without productCode (shows template structure only)
        const res = await fetch(`${MASTER_API}/inspection/report/${encodedNo}`, { headers: getAuthHeaders() });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        const withStdValue = rows.filter(
          (r) => r.standard_value !== null && r.standard_value !== undefined && String(r.standard_value).trim() !== ""
        );
        setReportData(withStdValue);
      }

    } catch (err) {
      console.error("RawMaterialInspectionReportView loadReport error:", err);
    }
  };

  const handleDownload = () => {
    const element = reportRef.current;
    if (!element) return;
    html2pdf()
      .set({
        margin: 10,
        filename: `${inspectionNo}_RawMaterialInspectionReport.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
      })
      .from(element)
      .save();
  };

  useImperativeHandle(ref, () => ({ handleDownload }));

  const groupByProcess = (data) => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, row) => {
      const process = row.process_name || "Others";
      if (!acc[process]) acc[process] = [];
      acc[process].push(row);
      return acc;
    }, {});
  };

  const groupedTests = groupByProcess(reportData);
  const header = reportData.length > 0 ? reportData[0] : {};

  const th = {
    border: "1px solid black", padding: "6px 8px", textAlign: "center",
    fontWeight: "bold", fontSize: "12px", background: "#fff",
  };
  const td = { border: "1px solid #000", padding: "4px", fontSize: "12px", verticalAlign: "middle" };

  return (
    <div>
      <div
        ref={reportRef}
        className="inspection-report-content"
        style={{ width: "1000px", margin: "20px auto", border: "2px solid black", background: "#fff", fontFamily: "Calibri" }}
      >
        {/* Company Header */}
        <div style={{ display: "flex", alignItems: "center", borderBottom: "2px solid black", padding: "12px 16px" }}>
          <img src={sbeeLogo} alt="SBEE" style={{ width: "150px", height: "auto" }} />
          <div style={{ flex: 1, textAlign: "center", fontSize: "26px", fontWeight: "bold", letterSpacing: "0.5px", fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif" }}>
            SBEE CABLES (INDIA) LIMITED
          </div>
        </div>

        {/* Report Title */}
        <div style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", borderBottom: "2px solid black", padding: "8px", letterSpacing: "0.5px" }}>
          {header.inspection_type || "RAW MATERIAL INSPECTION REPORT"}
        </div>

        {/* Info Header Table */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
           <tr>
  <td style={{ ...td, width: "33.33%" }}>
    SUPPLIER : {stockHeader.supplier_name || header.vendor_name || ""}
  </td>

  <td style={{ ...td, width: "33.33%" }}>
    GRN No : {extraHeader.grn_number || stockHeader.grn_number || ""}
    &nbsp;&nbsp;&nbsp;PO No : {extraHeader.po_number || stockHeader.po_number || ""}
  </td>

  <td style={{ ...td, width: "33.33%" }}>
    Date of Testing : {stockHeader.grn_date ? new Date(stockHeader.grn_date).toLocaleDateString("en-GB") : new Date().toLocaleDateString("en-GB")}
  </td>
</tr>

<tr>
  <td style={{ ...td, width: "33.33%" }}>
    INVOICE No : {extraHeader.invoice_no || ""}
  </td>

  <td style={{ ...td, width: "33.33%" }}>
    CATEGORY / SUB CATEGORY :
    {stockHeader.category || header.category || ""}
    {(stockHeader.sub_category || header.sub_category)
      ? ` / ${stockHeader.sub_category || header.sub_category}`
      : ""}
  </td>

  <td style={{ ...td, width: "33.33%" }}>
    Samples: {sampleLabels.length}
  </td>
</tr>
            <tr>
              <td style={{ ...td, width: "33.33%" }}>PRODUCT CODE : {stockHeader.product_code || header.product_code || ""}</td>
              <td style={{ ...td, width: "33.33%" }}>PRODUCT DESCRIPTION : {stockHeader.product_description || header.product_description || ""}</td>
              <td style={{ ...td, width: "33.33%" }}>BATCH No : {stockHeader.batch_number || ""}</td>
            </tr>
            <tr>
              <td style={{ ...td, width: "33.33%" }}>W.I. No : {extraHeader.wi_no || ""}</td>
              <td style={{ ...td, width: "33.33%" }}>Ref : {extraHeader.ref_no || ""}</td>
              <td style={{ ...td, width: "33.33%" }}>QTY : {formatQty(stockHeader.qty) || ""}</td>
            </tr>
            <tr>
              <td style={{ ...td, width: "33.33%" }}>Tested By : {extraHeader.tested_by || ""}</td>
              {(testType || header.inspection_type || "").toLowerCase() === "customer inspection" && (
                <>
                  <td style={{ ...td, width: "33.33%" }}>Witnessed By : {extraHeader.witnessed_by || ""}</td>
                  <td style={{ ...td, width: "33.33%" }}>Designation : {extraHeader.witnessed_designation || ""}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>

        {/* Test Results Table */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th rowSpan="2" style={{ ...th, width: "27%", textAlign: "left", paddingLeft: "10px" }}>PARTICULARS</th>
              <th rowSpan="2" style={{ ...th, width: "14%" }}>SPECIFIED VALUE</th>
              <th rowSpan="2" style={{ ...th, width: "7%" }}>UOM</th>
              <th colSpan={sampleLabels.length} style={{ ...th, background: "#f5f5f5" }}>SAMPLE</th>
              <th rowSpan="2" style={{ ...th, width: "9%" }}>STATUS</th>
              <th rowSpan="2" style={{ ...th, width: "13%" }}>REMARKS</th>
            </tr>
            <tr>
              {sampleLabels.map((label, i) => (
                <th key={i} style={{ ...th, width: `${63 / sampleLabels.length}%` }}>
                  {String(label).replace(/^Sample\.\s*/, "Sample ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedTests).map(([process, rows]) =>
              rows.map((row, index) => {
                const values = row.sample_values || [];
                // Sample Requirement = "Yes" is the only case that uses
                // Sample 2+ — same rule as the Edit/View modals in
                // Quality.js. Every other row is locked to Sample 1, so
                // later sample columns are left blank here even if stale
                // values exist for them.
                const singleSample = row.sample_requirement !== "Yes";
                return (
                  <tr key={`${process}-${index}`}>
                    <td style={{ ...td, paddingLeft: "20px" }}>{row.characteristic}</td>
                    <td style={{ ...td, textAlign: "center" }}>{row.standard_value || ""}</td>
                    <td style={{ ...td, textAlign: "center" }}>{row.uom || ""}</td>
                    {singleSample ? (
                      <td colSpan={sampleLabels.length} style={{ ...td, textAlign: "center" }}>{values[0] || ""}</td>
                    ) : (
                      sampleLabels.map((_, si) => (
                        <td key={si} style={{ ...td, textAlign: "center" }}>{values[si] || ""}</td>
                      ))
                    )}
                    <td style={{ ...td, textAlign: "center", fontWeight: "bold", textTransform: "capitalize" }}>
                      {row.manual_result || ""}
                    </td>
                    <td style={td}>{row.remarks || ""}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

      
      
      {/* Footer */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: "bold", width: "50%" }}>CHECKED BY : {checkedByName}</td>
              <td style={{ ...td, fontWeight: "bold", width: "50%" }}>APPROVED BY : {approvedByName}</td>
            </tr>
            <tr>
              <td style={{ ...td, height: "36px" }}>DESIGNATION</td>
              <td style={td}>DESIGNATION</td>
            </tr>
          </tbody>
        </table>

        {/* Format & Revision */}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", fontSize: "11px", borderTop: "1px solid black" }}>
          <div><b>Format No: {inspectionNo}</b></div>
          <div><b>Rev.No & Date:30/8/2026</b> </div>
        </div>
      </div>
    </div>
  );
});

export default RawMaterialInspectionReportView;